import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { teamApi } from '@/features/team/api';

/** Query keys are scoped by tenant, so switching organization never shows the previous one's data. */
export const teamKeys = {
  members: (tenantId: string) => ['team', tenantId, 'members'] as const,
  invitations: (tenantId: string) => ['team', tenantId, 'invitations'] as const,
  organization: (tenantId: string) => ['team', tenantId, 'organization'] as const,
};

/** Permissions the gateway requires for each read (api-gateway organization.routes.ts). */
export const TEAM_PERMISSIONS = { members: 'member:read', organization: 'org:read' } as const;

/**
 * Each query runs only when the user holds the permission the gateway checks, so a user without it
 * sees a "no access" state instead of a failed request.
 */
export function useTeamMembers() {
  const { user } = useAuth();
  const { can } = usePermission();
  const tenantId = user?.tenantId ?? '';
  const allowed = can(TEAM_PERMISSIONS.members);
  const query = useQuery({
    queryKey: teamKeys.members(tenantId),
    queryFn: () => teamApi.members(tenantId),
    enabled: allowed && tenantId !== '',
  });
  return { ...query, allowed };
}

export function useTeamInvitations() {
  const { user } = useAuth();
  const { can } = usePermission();
  const tenantId = user?.tenantId ?? '';
  const allowed = can(TEAM_PERMISSIONS.members);
  const query = useQuery({
    queryKey: teamKeys.invitations(tenantId),
    queryFn: () => teamApi.invitations(tenantId),
    enabled: allowed && tenantId !== '',
  });
  return { ...query, allowed };
}

export function useOrganizationProfile() {
  const { user } = useAuth();
  const { can } = usePermission();
  const tenantId = user?.tenantId ?? '';
  const allowed = can(TEAM_PERMISSIONS.organization);
  const query = useQuery({
    queryKey: teamKeys.organization(tenantId),
    queryFn: () => teamApi.organization(tenantId),
    enabled: allowed && tenantId !== '',
  });
  return { ...query, allowed };
}
